/**
 * useAdminAuth Hook
 * Manages admin authentication state and provides auth methods
 */

import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import {
  signInAdmin,
  signInWithGoogle,
  signOutAdmin,
  getCurrentAdmin,
  subscribeToAuthState,
} from '../services/adminAuthService';
import type { Admin, AdminSession } from 'foodhub-shared/types';

interface AdminAuthState {
  admin: Admin | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

// Global state to prevent multiple initializations
let globalAuthState: AdminAuthState | null = null;
let isInitialized = false;

export const useAdminAuth = () => {
  const [authState, setAuthState] = useState<AdminAuthState>(() => {
    if (globalAuthState) {
      return globalAuthState;
    }
    return {
      admin: null,
      user: null,
      loading: true,
      error: null,
      isAuthenticated: false,
    };
  });

  const updateGlobalState = useCallback((newState: AdminAuthState) => {
    globalAuthState = newState;
    setAuthState(newState);
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    if (isInitialized) {
      return;
    }
    isInitialized = true;

    // Subscribe to auth state changes
    const unsubscribe = subscribeToAuthState(async (user: User | null) => {
      try {
        if (user) {
          // User is signed in, get admin data
          const admin = await getCurrentAdmin(user);
          if (admin) {
            updateGlobalState({
              admin,
              user,
              loading: false,
              error: null,
              isAuthenticated: true,
            });
          } else {
            // User exists but no admin record
            updateGlobalState({
              admin: null,
              user: null,
              loading: false,
              error: 'Admin record not found',
              isAuthenticated: false,
            });
          }
        } else {
          // User is signed out
          updateGlobalState({
            admin: null,
            user: null,
            loading: false,
            error: null,
            isAuthenticated: false,
          });
        }
      } catch (error: any) {
        console.error('Auth state change error:', error);
        updateGlobalState({
          admin: null,
          user: null,
          loading: false,
          error: error.message || 'Authentication error',
          isAuthenticated: false,
        });
      }
    });

    return () => unsubscribe();
  }, [updateGlobalState]);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      try {
        updateGlobalState({
          ...authState,
          loading: true,
          error: null,
        });

        const session: AdminSession = await signInAdmin(email, password);

        updateGlobalState({
          admin: session.admin,
          user: null, // Will be set by auth state listener
          loading: false,
          error: null,
          isAuthenticated: true,
        });

        return true;
      } catch (error: any) {
        const errorMessage = error.message || 'Login failed';
        updateGlobalState({
          admin: null,
          user: null,
          loading: false,
          error: errorMessage,
          isAuthenticated: false,
        });
        return false;
      }
    },
    [authState, updateGlobalState]
  );

  const loginWithGoogle = useCallback(
    async (): Promise<boolean> => {
      try {
        updateGlobalState({
          ...authState,
          loading: true,
          error: null,
        });

        const session: AdminSession = await signInWithGoogle();

        updateGlobalState({
          admin: session.admin,
          user: null, // Will be set by auth state listener
          loading: false,
          error: null,
          isAuthenticated: true,
        });

        return true;
      } catch (error: any) {
        const errorMessage = error.message || 'Google Sign-In failed';
        updateGlobalState({
          admin: null,
          user: null,
          loading: false,
          error: errorMessage,
          isAuthenticated: false,
        });
        return false;
      }
    },
    [authState, updateGlobalState]
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      updateGlobalState({
        ...authState,
        loading: true,
      });

      await signOutAdmin();

      updateGlobalState({
        admin: null,
        user: null,
        loading: false,
        error: null,
        isAuthenticated: false,
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      updateGlobalState({
        ...authState,
        loading: false,
        error: error.message || 'Logout failed',
      });
    }
  }, [authState, updateGlobalState]);

  const clearError = useCallback(() => {
    updateGlobalState({
      ...authState,
      error: null,
    });
  }, [authState, updateGlobalState]);

  return {
    admin: authState.admin,
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    isAuthenticated: authState.isAuthenticated,
    login,
    loginWithGoogle,
    logout,
    clearError,
  };
};

export default useAdminAuth;

